'use client';

import { useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { AUTH_ERROR_CODES } from '@impact/auth';
import { trpcVanilla } from '@/lib/trpc';
import { type VehicleFormData, type CompleteRegistrationFormData } from '../schemas/registration';
import { trackRego } from '../lib/registration-analytics';

interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  defaultBlog: { title: string; content: string };
  eventId: string;
  joinWaitlist: boolean;
}

interface LoginData {
  email: string;
  password: string;
  defaultBlog: { title: string; content: string };
  eventId: string;
  joinWaitlist: boolean;
}

interface Entrant {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  entrantType: string;
  isActive: boolean;
}

function isDuplicateSignupErrorMessage(message: string): boolean {
  const n = message.toLowerCase();
  return (
    n.includes('already exists') ||
    n.includes('duplicate key value') ||
    n.includes('failed query: insert into "entrants"')
  );
}

export interface UseRegistrationAuthOptions {
  /** Vehicle type recorded against new registrations. Defaults to 'motorcycle' (DGR/DGD behaviour). */
  vehicleType?: 'motorcycle' | 'car';
}

export function useRegistrationAuth(options?: UseRegistrationAuthOptions) {
  const vehicleType = options?.vehicleType ?? 'motorcycle';
  const tAuth = useTranslations('auth');
  const [authMode, setAuthMode] = useState<'signup' | 'login' | 'complete-registration'>('signup');
  const [emailStatus, setEmailStatus] = useState<
    'unchecked' | 'checking' | 'new' | 'existing' | 'existing-registered'
  >('unchecked');
  const [checkedEmail, setCheckedEmail] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authErrorCode, setAuthErrorCode] = useState<
    'duplicate-email' | 'legacy-reset-required' | null
  >(null);
  const [entrant, setEntrant] = useState<Entrant | null>(null);
  const [vehicle, setVehicle] = useState<VehicleFormData | null>(null);
  const [prefilledProfile, setPrefilledProfile] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  } | null>(null);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
    setAuthErrorCode(null);
  }, []);

  const resetEmailCheck = useCallback(() => {
    setEmailStatus('unchecked');
    setCheckedEmail(null);
  }, []);

  const checkEmail = useCallback(
    async (email: string, eventId?: string) => {
      const isRetry = checkedEmail !== null;
      setEmailStatus('checking');
      setCheckedEmail(email);
      try {
        const result = await trpcVanilla.publicEntrant.checkEmail.query({
          email,
          eventId: eventId || undefined,
        });
        if (result.exists && result.hasActiveRegistration) {
          setEmailStatus('existing-registered');
          trackRego('rego_email_checked', { result: 'existing-registered', is_retry: isRetry });
        } else if (result.exists) {
          setEmailStatus('existing');
          trackRego('rego_email_checked', { result: 'existing', is_retry: isRetry });
        } else {
          setEmailStatus('new');
          trackRego('rego_email_checked', { result: 'new', is_retry: isRetry });
        }
      } catch (e) {
        setEmailStatus('new');
        trackRego('rego_error', {
          step: 'auth',
          error_type: 'email_check_failed',
          error_message: e instanceof Error ? e.message : 'Unknown error',
        });
      }
    },
    [checkedEmail],
  );

  const signup = useCallback(
    async (data: SignupData) => {
      setIsAuthLoading(true);
      setAuthError(null);
      setAuthErrorCode(null);
      try {
        const authRes = await fetch('/api/auth/sign-up/email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: data.email,
            password: data.password,
            name: `${data.firstName} ${data.lastName}`,
          }),
          credentials: 'include',
        });

        if (!authRes.ok) {
          const err = await authRes.json();
          throw new Error(err.message || tAuth('error.createAccountFailed'));
        }

        const newEntrant = await trpcVanilla.publicEntrant.create.mutate({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
        });

        if (!data.eventId) {
          throw new Error(tAuth('error.selectRide'));
        }

        await trpcVanilla.publicEntrant.ensureRegistration.mutate({
          eventId: data.eventId,
          joinWaitlist: data.joinWaitlist,
          defaultBlog: data.defaultBlog,
          vehicle: {
            vehicleType,
            vehicleMake: data.vehicleMake,
            vehicleModel: data.vehicleModel,
            vehicleYear: Number.isNaN(parseInt(data.vehicleYear, 10))
              ? undefined
              : parseInt(data.vehicleYear, 10),
          },
        });

        setEntrant(newEntrant);
        setVehicle({
          vehicleType,
          vehicleMake: data.vehicleMake,
          vehicleModel: data.vehicleModel,
          vehicleYear: parseInt(data.vehicleYear, 10),
        });
        setIsAuthLoading(false);
        return { success: true };
      } catch (e) {
        const rawMessage = e instanceof Error ? e.message : '';
        trackRego('rego_error', {
          step: 'auth',
          error_type: 'signup_failed',
          error_message: rawMessage,
        });
        setAuthError(rawMessage || tAuth('signupFailed'));
        setAuthErrorCode(isDuplicateSignupErrorMessage(rawMessage) ? 'duplicate-email' : null);
        setIsAuthLoading(false);
        return { success: false };
      }
    },
    [tAuth, vehicleType],
  );

  const login = useCallback(
    async (data: LoginData) => {
      setIsAuthLoading(true);
      setAuthError(null);
      setAuthErrorCode(null);
      try {
        const authRes = await fetch('/api/auth/sign-in/email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: data.email, password: data.password }),
          credentials: 'include',
        });

        if (!authRes.ok) {
          const err = ((await authRes.json().catch(() => null)) ?? {}) as {
            code?: string;
            message?: string;
          };
          const message =
            err.code === AUTH_ERROR_CODES.legacyPasswordResetRequired
              ? 'legacy-reset-required'
              : err.message || tAuth('error.invalidCredentials');
          throw new Error(message);
        }

        const existingEntrant = await trpcVanilla.publicEntrant.getByUser.query();

        if (existingEntrant && data.eventId) {
          try {
            await trpcVanilla.publicEntrant.ensureRegistration.mutate({
              eventId: data.eventId,
              joinWaitlist: data.joinWaitlist,
              defaultBlog: data.defaultBlog,
            });
          } catch (e) {
            const message = e instanceof Error ? e.message : '';
            const isAlreadyRegistered =
              message.includes('already registered') ||
              (e as { data?: { code?: string } }).data?.code === 'CONFLICT';
            if (!isAlreadyRegistered) throw e;
          }
        }

        if (!existingEntrant) {
          const meData = await trpcVanilla.authMe.me.query();
          setAuthMode('complete-registration');
          setPrefilledProfile({
            email: meData.user.email,
            firstName: meData.profile?.firstName ?? '',
            lastName: meData.profile?.lastName ?? '',
            phone: null,
          });
          setAuthError(null);
          setAuthErrorCode(null);
          setIsAuthLoading(false);
          return { success: false, hasEntrant: false };
        }

        setEntrant(existingEntrant);
        setIsAuthLoading(false);
        return { success: true, hasEntrant: true };
      } catch (e) {
        const rawMessage = e instanceof Error ? e.message : '';
        trackRego('rego_error', {
          step: 'auth',
          error_type: 'login_failed',
          error_message: rawMessage,
        });
        setAuthError(rawMessage || tAuth('loginFailed'));
        setAuthErrorCode(rawMessage === 'legacy-reset-required' ? 'legacy-reset-required' : null);
        setIsAuthLoading(false);
        return { success: false, hasEntrant: false };
      }
    },
    [tAuth],
  );

  const completeRegistration = useCallback(
    async (
      data: CompleteRegistrationFormData & {
        defaultBlog: { title: string; content: string };
        eventId: string;
        joinWaitlist: boolean;
      },
    ) => {
      setIsAuthLoading(true);
      setAuthError(null);
      setAuthErrorCode(null);
      try {
        const newEntrant = await trpcVanilla.publicEntrant.create.mutate({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
        });

        if (!data.eventId) {
          throw new Error(tAuth('error.selectRide'));
        }

        await trpcVanilla.publicEntrant.ensureRegistration.mutate({
          eventId: data.eventId,
          joinWaitlist: data.joinWaitlist,
          defaultBlog: data.defaultBlog,
          vehicle: {
            vehicleType,
            vehicleMake: data.vehicleMake,
            vehicleModel: data.vehicleModel,
            vehicleYear: Number.isNaN(parseInt(data.vehicleYear, 10))
              ? undefined
              : parseInt(data.vehicleYear, 10),
          },
        });

        setEntrant(newEntrant);
        setVehicle({
          vehicleType,
          vehicleMake: data.vehicleMake,
          vehicleModel: data.vehicleModel,
          vehicleYear: parseInt(data.vehicleYear, 10),
        });
        setPrefilledProfile(null);
        setIsAuthLoading(false);
        return { success: true };
      } catch (e) {
        const rawMessage = e instanceof Error ? e.message : '';
        trackRego('rego_error', {
          step: 'auth',
          error_type: 'complete_registration_failed',
          error_message: rawMessage,
        });
        setAuthError(rawMessage || tAuth('error.registrationFailed'));
        setAuthErrorCode(isDuplicateSignupErrorMessage(rawMessage) ? 'duplicate-email' : null);
        setIsAuthLoading(false);
        return { success: false };
      }
    },
    [tAuth, vehicleType],
  );

  return {
    authMode,
    setAuthMode,
    emailStatus,
    setEmailStatus,
    checkedEmail,
    checkEmail,
    resetEmailCheck,
    isAuthLoading,
    authError,
    authErrorCode,
    clearAuthError,
    entrant,
    setEntrant,
    vehicle,
    prefilledProfile,
    signup,
    login,
    completeRegistration,
  };
}

export type UseRegistrationAuthResult = ReturnType<typeof useRegistrationAuth>;
